/**
 * The ledger: every booked document becomes a journal entry of balanced debit and credit lines.
 * A PocketBase hook (`server/pb_hooks/ledger.pb.js`) writes it in the same transaction as the
 * document; this file decides what the lines are.
 *
 * It runs in two places: here under Jest, and bundled into `server/pb_hooks/lib/ledger.js` inside
 * PocketBase's JS engine (goja). Keep it - and everything it imports - free of `\p{...}` regexes,
 * zod, Intl and async code: goja cannot run them. `pnpm --filter server build:hooks` rebuilds
 * the bundle, and a server test fails while it is stale.
 *
 * Amounts are whole cents. The accounts are the Einheitskontenrahmen's: the document's category
 * gives the expense or revenue account, 2500 Vorsteuer and 3500 Umsatzsteuer take the tax, 3300
 * Lieferverbindlichkeiten and 2000 Lieferforderungen stand in for the Personenkonten (the partner
 * travels on the entry), and a Barbeleg is paid from the cash or card account.
 */
import { relevantDate, type AccountingBasis, type UvaDocument } from "@/lib/at/uva";
import { round2, vatFromNet, type TaxTreatment } from "@/lib/at/vat";
import { bookingLines } from "@/lib/export/common";

export const RECEIVABLES = "2000";
export const INPUT_VAT = "2500";
export const PAYABLES = "3300";
export const OUTPUT_VAT = "3500";

export type LedgerDocument = {
  id: string;
  workspace: string;
  kind: "ER" | "AR" | "KA";
  status: UvaDocument["status"];
  docNumber?: string;
  taxTreatment?: TaxTreatment | "";
  issueDate?: string;
  paidDate?: string;
  paymentStatus?: string;
  currency?: string;
  vatLines?: unknown;
  grossTotal?: number;
  tip?: number;
  partnerName?: string;
  partnerVatId?: string;
};

export type PostingContext = {
  /** The account of the document's category; empty when it has none. */
  account: string;
  basis: AccountingBasis;
  /** Kleinunternehmer: no input tax, so the tax is part of the cost. */
  smallBusiness: boolean;
  /** Kassa, for a Barbeleg nobody paid by card. */
  cashAccount: string;
  /** The ledger account of the card a Barbeleg was charged to, when a charge points to it. */
  paidFrom?: string;
};

/**
 * What a line is for: the UVA reads `base` and `tax`, the partner or till takes `counter`. The
 * money side (money.ts) adds `money` for a bank, card or cash account, `settlement` for a payment
 * clearing a supplier or customer, and `other` for private, Finanzamt, transfer, opening and asset
 * lines. Income and expense lines are `base` wherever they come from.
 */
export type LineRole = "base" | "tax" | "tip" | "counter" | "money" | "settlement" | "other";

export type LedgerLine = {
  account: string;
  debit: number;
  credit: number;
  role: LineRole;
  /** The VAT rate of a base or tax line, 0 otherwise. */
  vatRate: number;
};

export type Posting = {
  /** ISO day; for a document the one that puts it into its UVA period (`relevantDate`). */
  date: string;
  /** Empty for entries that are not a document's (money.ts). */
  kind: LedgerDocument["kind"] | "";
  taxTreatment: TaxTreatment | "";
  docNumber: string;
  partnerName: string;
  partnerVatId: string;
  lines: LedgerLine[];
};

/** Why a booked document has no entry. The same gaps keep it out of the accountant's export. */
export type PostingProblem =
  | "foreign_currency"
  | "no_account"
  | "no_vat_lines"
  | "no_date"
  /** Cash basis: an open invoice is posted once it is paid (`relevantDate`). */
  | "unpaid";

const cents = (value: number) => Math.round(round2(value) * 100);

/**
 * The entry a document should have right now: null while it is not booked, a problem when it is
 * booked but cannot be posted safely.
 */
export function postingFor(
  document: LedgerDocument,
  context: PostingContext,
): Posting | { problem: PostingProblem } | null {
  if (document.status !== "booked") return null;
  const normalized = { ...document, taxTreatment: document.taxTreatment || "standard" } as const;
  const treatment: TaxTreatment = normalized.taxTreatment;
  const vatLines = bookingLines(normalized);
  const date = (relevantDate(normalized, context.basis) ?? "").slice(0, 10);
  if ((document.currency || "EUR") !== "EUR") return { problem: "foreign_currency" };
  if (!context.account) return { problem: "no_account" };
  if (vatLines.length === 0) return { problem: "no_vat_lines" };
  if (!date)
    return { problem: context.basis === "cash" && document.issueDate ? "unpaid" : "no_date" };

  const lines: LedgerLine[] = [];
  // A positive amount lands on `side`, a negative one (a credit note) on the other.
  const book = (
    account: string,
    amount: number,
    side: "debit" | "credit",
    role: LineRole,
    vatRate = 0,
  ) => {
    if (amount === 0) return;
    const debit = (side === "debit") === amount > 0;
    const value = Math.abs(amount);
    lines.push({ account, debit: debit ? value : 0, credit: debit ? 0 : value, role, vatRate });
  };
  const { account, smallBusiness } = context;

  if (document.kind === "AR") {
    for (const line of vatLines) {
      const net = cents(line.net);
      const vat = cents(line.vat);
      if (treatment === "standard" && !smallBusiness) {
        book(account, net, "credit", "base", line.rate);
        book(OUTPUT_VAT, vat, "credit", "tax", line.rate);
      } else {
        book(account, net + vat, "credit", "base", line.rate);
      }
    }
  } else {
    for (const line of vatLines) {
      const net = cents(line.net);
      const vat = cents(line.vat);
      if (treatment === "reverse_charge" || treatment === "eu_intra_community") {
        // The supplier charged no VAT: we owe it, and deduct it again unless we cannot deduct.
        const tax = line.vat > 0 ? vat : cents(vatFromNet(line.net, line.rate));
        book(account, net, "debit", "base", line.rate);
        book(smallBusiness ? account : INPUT_VAT, tax, "debit", "tax", line.rate);
        book(OUTPUT_VAT, tax, "credit", "tax", line.rate);
      } else if ((treatment === "standard" || treatment === "export") && !smallBusiness) {
        // Austrian input tax, or import VAT paid at the border: both deductible.
        book(account, net, "debit", "base", line.rate);
        book(INPUT_VAT, vat, "debit", "tax", line.rate);
      } else {
        book(account, net + vat, "debit", "base", line.rate);
      }
    }
    // A tip carries no VAT and is the same kind of cost as the receipt.
    book(account, cents(document.tip ?? 0), "debit", "tip");
  }

  // The partner or the till takes the difference, so every entry balances by construction.
  const balance = lines.reduce((sum, line) => sum + line.debit - line.credit, 0);
  const counter =
    document.kind === "KA"
      ? context.paidFrom || context.cashAccount
      : document.kind === "AR"
        ? RECEIVABLES
        : PAYABLES;
  const before = lines.length;
  book(counter, balance, "credit", "counter");
  if (lines.length > before) lines.unshift(lines.pop()!);

  const partner = document.kind !== "KA";
  return {
    date,
    kind: document.kind,
    taxTreatment: treatment,
    docNumber: document.docNumber ?? "",
    partnerName: partner ? (document.partnerName ?? "") : "",
    partnerVatId: partner ? (document.partnerVatId ?? "").replace(/\s+/g, "").toUpperCase() : "",
    lines,
  };
}

export function isBalanced(lines: LedgerLine[]): boolean {
  return lines.reduce((sum, line) => sum + line.debit - line.credit, 0) === 0;
}
